import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState, JSX } from 'react';

import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { LeaderboardActions, LeaderboardMultiplier } from '@/types/leaderboard.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import Modal from '@ui/modal/modal';
import Switch from '@ui/switch/switch';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { UpdateActionCustomPoint } from '@/connection/leaderboard';
import { actionLabelMap } from './types';
import { getInitialData } from '@/store/slices/leaderboard/initial';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { selectProjectEventId } from '@/store/slices/project/selectors';
import { getLeaderboardMainActions } from '@/store/slices/leaderboard/selectors';
import { updateActionPoints } from '@/store/slices/leaderboard';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import toastTrigger from '@ui/toast/toast-trigger';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

interface PointEditModalProps {
  open: boolean;
  closeFn: Dispatch<SetStateAction<boolean>>;
  initialActions: LeaderboardMultiplier[];
}

export const PointsEditModal = ({ open, closeFn, initialActions }: PointEditModalProps): JSX.Element => {
  const eventId = useAppSelector(selectProjectEventId);
  const activeChannel = useAppSelector(getActiveChannel);
  const loadedActions = useAppSelector(getLeaderboardMainActions);

  const [actions, setActions] = useState<LeaderboardMultiplier[]>(
    initialActions.length ? initialActions : getInitialData(eventId)
  );
  const [saving, setSaving] = useState(false);

  const dispatch = useAppDispatch();

  // if any of the actions have a multiplier less than 1, disbale save option
  const valid = useMemo(() => {
    let valid = true;
    actions.forEach((action) => {
      if (action.multiplier < 1 && action.is_on) {
        valid = false;
      }
    });
    return valid;
  }, [actions]);

  // a toggled on action will appear in the My Leaderboard section
  // a toggled off action will appear in the All Actions section
  const toggleAction = useCallback(
    (id: LeaderboardActions, isOn: boolean) => {
      const newActions = actions.map((action) => {
        if (action.action === id) {
          return { ...action, is_on: isOn };
        }
        return action;
      });
      setActions(newActions);
    },
    [actions]
  );

  useEffect(() => {
    if (!loadedActions.length) {
      setActions(getInitialData(eventId));
    } else {
      setActions(loadedActions);
    }
  }, [eventId, loadedActions]);

  const handleMultiplierChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, actionType: LeaderboardActions) => {
      e.stopPropagation();
      e.preventDefault();
      const { target } = e;
      const scoreSet = actions.map((action) => {
        if (action.action === actionType) {
          return { ...action, multiplier: Math.floor(Number(target.value)) };
        }
        return action;
      });
      setActions(scoreSet);
    },
    [actions]
  );

  const handleSubmit = async () => {
    setSaving(true);
    try {
      //Make sure the user toggle on the action and set the point for it before submitting. The default multiplier value is always 1. They have to set the point for the action or have to toggle it off.
      const isToggleOnWithoutValueSet = actions.some((action) => action.is_on && action.multiplier < 1);

      if (isToggleOnWithoutValueSet) {
        toastTrigger(
          <div>
            <h6>Error submitting points</h6>
            <p>
              Some or all of the actions are toggled on but the point values are not set. Please update or toggle the
              unset action(s) off.
            </p>
          </div>,
          { type: 'error' }
        );

        return;
      }

      if (activeChannel) {
        const finalActionsSent: LeaderboardMultiplier[] = [];
        for (const action of actions) {
          //the existence backend route only take one action per time.
          const result = await UpdateActionCustomPoint(action, activeChannel);
          if (result) {
            finalActionsSent.push(result);
          }
        }
        //update the updated array of actions.
        dispatch(updateActionPoints(finalActionsSent));
      }
      closeFn(false);
    } catch (error) {
      toastTrigger(
        <div>
          <h6>Error updating points</h6>
          <p>We were unable to update the action points. Please try again</p>
        </div>,
        { type: 'error' }
      );
      console.error(error);
    }

    setSaving(false);
  };

  const cancel = () => {
    if (!loadedActions.length) {
      setActions(getInitialData(eventId));
    } else {
      setActions(loadedActions);
    }
    closeFn(false);
  };

  // When we toggle on the action, display it on the leaderboard section.
  const renderLeaderboardAction = useCallback(
    (action: LeaderboardMultiplier): JSX.Element => (
      <Container background="var(--gray-900)" className="leaderboard" key={action.action}>
        <div className="title">
          <Text tag="span" className="action">
            {actionLabelMap[action.action]}
          </Text>
        </div>
        <div className="toggle-and-points flex gap-4">
          <OptionalComponent display={action.is_on}>
            <InputWithOverlay
              status={action.multiplier < 1 ? 'error' : undefined}
              inputProps={{
                type: 'number',
                min: 1,
                className: 'score-point-input',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleMultiplierChange(e, action.action),
                value: String(action.multiplier),
                disabled: saving,
              }}
            />
          </OptionalComponent>
          <Switch value={action.action} onClick={toggleAction} on={action.is_on} key={action.action} />
        </div>
      </Container>
    ),
    [handleMultiplierChange, toggleAction, saving]
  );

  const renderLists = useMemo(
    () => (
      <Container borderRadius="12px 12px 0 0" padding="24px 40px">
        <div className="leaderboard-wrapper">
          <div className="my-leaderboard-div">
            <Text tag="p" className="modal-subtitle action-subtitle">
              MY LEADERBOARD
            </Text>
            <Text className="all-items">{actions.map((action) => renderLeaderboardAction(action))}</Text>
          </div>
        </div>
      </Container>
    ),
    [actions, renderLeaderboardAction]
  );

  return (
    <div className="leader-board-edit-point-modal">
      <Modal
        open={open}
        RenderHeaderContent={() => {
          return (
            <Container borderRadius="12px 12px 0 0" padding="40px 40px 8px 40px">
              <Text tag="h3" className="H3">
                Leaderboard
              </Text>

              <Text tag="h5" className="L1">
                Add or edit score settings
              </Text>
            </Container>
          );
        }}
        onRequestClose={cancel}
        contentWrapperClassName="edit-points-modal-leaderboard"
        customSize={{
          width: '640px',
          height: '525px',
          minHeight: '100px',
          maxHeight: 'calc(100% - 32px)',
          maxWidth: 'calc(100% - 32px)',
        }}
        RenderFooterContent={() => (
          <Container className="flex fd-row jc-flex-end ai-center gap-8" padding="8px 16px">
            <Button hoverBackgroundColor="var(--gray-700)" onClick={cancel}>
              <Text>Cancel</Text>
            </Button>
            <Button
              width="94px"
              height="32px"
              onClick={handleSubmit}
              disabled={saving || !valid}
              className="flex ai-center jc-center"
              loading={saving}
              backgroundColor={getThemeColorCSSVariable()}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Save
              </Text>
            </Button>
          </Container>
        )}
      >
        {renderLists}
      </Modal>
    </div>
  );
};
